import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { readPendingWatch, clearPendingWatch, MIN_AWAY_MS } from '../lib/watchReturn'
import { isCaughtUp, effectiveLastAired } from '../lib/episode'
import { nextMark } from '../lib/episodeProgress'
import { supabase } from '../lib/supabase'
import { ClapboardClosed } from './ClapboardIcon'
import { Z } from '../lib/zLayers'

// ── WatchReturnPrompt ─────────────────────────────────────────────────────────
// When you come back to NextEp after tapping "Watch on <service>", offer a
// one-tap mark of the episode you went to watch. We can't know what you actually
// watched, so we assume your up-next (you launched to watch what's next) and
// resolve it from current progress at prompt time. Marking runs the SAME path as
// every other mark (saveProgress + watchedEp) → UndoToast/rating fire as usual.
//
// Restyled to the 17 toast language (2026-09-19, Jason: "it's from v16") — was a
// hand-rolled card with a hardcoded navy gradient (ignored the theme), a raw
// zIndex, and a popcorn emoji. Now built from the same tokens/pattern as
// UndoToast and RatingSavedToast: color-mix amber card, --toast-* tokens, the
// clapboard glyph everywhere else in the app uses for "mark watched" (marking is
// slating a take, not ticking a box — same rule that toast follows).
//
// Double-prompt guard (2026-09-20, Jason: "There are now two prompts asking if
// a show is completed"): this prompt and WatchingCheckIn both watch for "you
// came back to a show you went to watch" via two independent heuristics (a
// localStorage pending-watch flag here vs. an overdue watch_sessions row
// there) and render into the identical fixed bottom slot — dismissing one
// revealed the other underneath, same show, same question, worded differently.
// Since a watch_sessions row is the more precise signal (it names the exact
// episode a tap started; this one just assumes "current next"), skip firing
// here whenever one is open for the same show — WatchingCheckIn already owns
// that moment.
export function WatchReturnPrompt({ wl, user }) {
  const [prompt, setPrompt] = useState(null)   // { showId, showName, service, season, episode }

  useEffect(() => {
    const check = async () => {
      if (document.visibilityState !== 'visible' || prompt) return
      const p = readPendingWatch()
      if (!p) return
      // Came straight back → a bounce, not a watch. Drop it silently.
      if (Date.now() - p.ts < MIN_AWAY_MS) { clearPendingWatch(); return }
      const prog = wl?.progress?.[p.showId]
      if (!prog) { clearPendingWatch(); return }   // can't name the episode → skip
      // A finished row stores the finale itself (not next-to-watch); offering to mark
      // it again publishes a second "watched" for an episode already done.
      const st = wl?.statuses?.[p.showId]
      if (st === 'completed' || st === 'dropped') { clearPendingWatch(); return }
      // Caught up? The "next" is a phantom unaired episode — don't offer to mark it.
      const show = wl?.items?.find(s => s.id === p.showId)
      const lastAired = effectiveLastAired(show)
      if (lastAired && isCaughtUp(prog, lastAired)) { clearPendingWatch(); return }
      if (user?.id) {
        const { data } = await supabase.from('watch_sessions').select('id')
          .eq('user_id', user.id).eq('show_id', p.showId).is('ended_at', null).limit(1)
        if (data?.length) { clearPendingWatch(); return }
      }
      setPrompt({ ...p, season: prog.season, episode: prog.episode })
    }
    document.addEventListener('visibilitychange', check)
    window.addEventListener('focus', check)
    check()  // catch the case where we returned before this mounted
    return () => {
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('focus', check)
    }
  }, [prompt, wl, user?.id])

  if (!prompt) return null

  const dismiss = () => { clearPendingWatch(); setPrompt(null) }
  const mark = () => {
    const prog = wl?.progress?.[prompt.showId]
    const show = wl?.items?.find(s => s.id === prompt.showId)
    if (prog && prog.season === prompt.season && prog.episode === prompt.episode) {
      // nextMark, not raw prog-as-watchedEp: it normalizes the caught-up overshoot
      // marker so a newly-listed season can't produce a ghost-episode activity.
      const { prog: nextProg, opts } = nextMark(show, prog)
      wl.saveProgress(prompt.showId, nextProg, opts)
    }
    clearPendingWatch(); setPrompt(null)
  }

  return createPortal((
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 'calc(var(--nav-h) + 8px)',
      zIndex: Z.toast, maxWidth: 460, margin: '0 auto', fontFamily: 'var(--font)',
      background: 'linear-gradient(135deg, color-mix(in srgb, var(--amber) 14%, #14141f), color-mix(in srgb, var(--amber) 6%, #0d0d16))',
      border: '1px solid color-mix(in srgb, var(--amber) 40%, transparent)', borderRadius: 'var(--toast-radius)',
      boxShadow: '0 14px 38px rgba(0,0,0,0.55), 0 4px 18px rgba(var(--amber-rgb),0.18), inset 0 1px 0 rgba(var(--ink-rgb),0.06)',
      backdropFilter: 'blur(16px) saturate(1.3)', WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
      padding: '12px 16px 13px',
      '--nxa': 'wrpIn 0.3s cubic-bezier(0.22,1.2,0.36,1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          width: 'var(--toast-chip-size)', height: 'var(--toast-chip-size)', borderRadius: 'var(--radius-full)',
          background: 'rgba(var(--amber-rgb),0.14)', border: '1px solid rgba(var(--amber-rgb),0.35)',
        }}><ClapboardClosed size={20} color="var(--amber)" /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: 'var(--amber)', fontSize: 'var(--toast-eyebrow-size)', fontWeight: 'var(--toast-eyebrow-weight)', letterSpacing: 'var(--toast-eyebrow-tracking)', textTransform: 'uppercase', marginBottom: 2 }}>
            Back{prompt.service ? ` from ${prompt.service}` : ''}?
          </div>
          <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Mark <span style={{ fontWeight: 700 }}>{prompt.showName || 'this show'}</span> S{prompt.season}·E{prompt.episode} watched?
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 9 }}>
        {/* Equal-weight buttons: marking-watched is real progress mutation, so it
            shouldn't be the oversized reflex-tap target for someone just returning to
            the app and dismissing a prompt. Both are one deliberate tap; UndoToast
            still covers a mistap. */}
        <button onClick={dismiss} style={{
          flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          padding: '11px 0', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 700, fontFamily: 'var(--font)',
        }}>Not yet</button>
        <button onClick={mark} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: 'var(--amber)', border: 'none', borderRadius: 'var(--radius)',
          padding: '11px 0', cursor: 'pointer', color: 'var(--amber-ink)', fontSize: 13.5, fontWeight: 800, fontFamily: 'var(--font)',
          boxShadow: '0 4px 14px rgba(var(--amber-rgb),0.34)',
        }}><ClapboardClosed size={15} color="var(--amber-ink)" />Mark watched</button>
      </div>
      <style>{'@keyframes wrpIn{0%{opacity:0;transform:translateY(20px) scale(0.96)}100%{opacity:1;transform:translateY(0) scale(1)}}'}</style>
    </div>
  ), document.body)
}
